import { useState, useEffect } from 'react';
import { Typography, Box, Button } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { SharedContainer } from '../../components/shared-container/container';
import { SharedCard } from '../../components/shared-card/card';
import OverlayLoader from '../../components/overlayLoader';
import Navbar from '../../components/shared-menu/Navbar';
import { useAuth } from '../../services/auth/AuthContext';
import { DIGIO_ENVIRONMENT } from '../../config';
import { useTranslation } from 'react-i18next';
import axios from 'axios';

import DigioService from '../../services/api/digio';

export default function ESignPage() {

  const { refreshUserData } = useAuth();

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackBarMessage, setSnackBarMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState('info');

  const handleClose = () => {
    setShowSnackbar(false);
    setSnackBarMessage('');
    setSnackSeverity('');
  };

  const [digioInstance, setDigioInstance] = useState(null);
  const [isDigioLoading, setIsDigioLoading] = useState(false);

  const [identifierValue, setIdentifierValue] = useState(null);

  const fetchDocumentDetails = async () => {
    try {
      setLoading(true);
      const data = await DigioService.createSignRequest();
      if ( !data || !data.id || !data.signing_parties || data.signing_parties.length === 0) {
        throw new Error(
          data.message ||
          "Invalid response from Digio API"
        );
      }
      var identifier = data.signing_parties[0].identifier;
      for (const party of data.signing_parties) {
        if (party.signature_type === 'aadhaar') {
          setIdentifierValue(party.identifier);
          identifier = party.identifier;
          break; // Assuming we only need the first individual party
        }
      }
      return { id: data.id, identifier: identifier }; // <-- return values
    } catch (error) {
      const is_axios_instance = error instanceof axios.AxiosError;
      const data = is_axios_instance ? error.response.data : error;
      console.error("Error fetching document details:", error);
      setSnackBarMessage(data.message || 'Failed to fetch document details');
      setSnackSeverity('error');
      setShowSnackbar(true);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {

    const queryParams = new URLSearchParams(window.location.search);
    const status = queryParams.get('status');
    const digioDocId = queryParams.get('digio_doc_id');
    const message = queryParams.get('message');

    function validateDocumentSignature(docId) {
      // Use these parameters if they exist
      if (status && digioDocId) {
        // Handle redirect response based on status
        if (status === 'success') {
          setSnackBarMessage(message || 'Document signed successfully');
          setSnackSeverity('success');
          // Additional success handling
          DigioService.validateDocumentSignature(digioDocId).then((responseData) => {
            if (responseData.status === 'signed_by_connector') {
              setSnackBarMessage('Document is verified successfully');
              setSnackSeverity('success');
              setShowSnackbar(true);
              // Status is now approved. Reload the user and ProtectedRoute moves
              // /esign on to /dashboard; navigating with the stale awaiting_esign
              // status bounced straight back here.
              refreshUserData();
            } else {
              setSnackBarMessage(responseData.message || 'Document verification failed');
              setSnackSeverity('error');
              setShowSnackbar(true);
            }
          })
        } else if (status === 'error' || status === 'cancel') {
          setSnackBarMessage(message || 'Signing failed or was cancelled');
          setSnackSeverity('error');
          setIsDigioLoading(false);
          setShowSnackbar(true);
        }
      }
    }

    if (status && digioDocId) {
      validateDocumentSignature(digioDocId);
    }
  }, []);

  useEffect(() => {
    // Load Digio SDK script
    const script = document.createElement('script');
    script.src = 'https://app.digio.in/sdk/v11/digio.js';
    script.async = true;
    script.onload = () => {
      console.log(DIGIO_ENVIRONMENT);
      // Initialize Digio instance after script loads
      if (window.Digio) {
        const options = {
          environment: DIGIO_ENVIRONMENT,
          logo: "https://s3.ap-south-1.amazonaws.com/www.optimocapital.in/img/logo.svg",
          is_redirection_approach: true,
          redirect_url: window.location.origin + "/esign",
          is_iframe: false,
          theme: {
            primaryColor: "#1F002A",
            secondaryColor: "#444444"
          }
        };

        setDigioInstance(new window.Digio(options));
        setIsDigioLoading(false);
      }
    };

    document.body.appendChild(script);

    // Cleanup
    return () => {
      if (script.parentNode) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handleDigioSign = async () => {
    if (!digioInstance) {
      setSnackBarMessage('Digio SDK is not loaded yet');
      setSnackSeverity('error');
      setShowSnackbar(true);
      return;
    }

    setLoading(true);

    try {
      // Get document details directly
      const docDetails = await fetchDocumentDetails();
      console.log("Document Details:", docDetails);
      if ( docDetails && docDetails.id && docDetails.identifier) {
        digioInstance.init();
        digioInstance.submit(docDetails.id, docDetails.identifier, {
          callback: (response) => {
            setLoading(false);
            if (response.hasOwnProperty("error_code")) {
              setSnackBarMessage('Error occurred in signing process');
              setSnackSeverity('error');
            } else {
              setSnackBarMessage('Signing completed successfully');
              setSnackSeverity('success');
            }
            setShowSnackbar(true);
          }
        });
      }
    } catch (error) {
      setLoading(false);
      setSnackBarMessage('Failed to initiate signing process');
      setSnackSeverity('error');
      setShowSnackbar(true);
    }
  };

  return (
      <>
        <Snackbar open={showSnackbar} autoHideDuration={3000} onClose={handleClose}>
          <Alert onClose={handleClose} severity={snackSeverity} sx={{ width: '100%' }}>
            <Typography>{snackBarMessage}</Typography>
          </Alert>
        </Snackbar>
        {loading && <OverlayLoader open={loading} text="" />}
        <Navbar isTransparent />
        <SharedContainer className="custom-class" direction="row" justifyContent="space-between" sx={{ flexWrap: 'wrap' }}>
          <SharedCard sx={{ width: '800px' }}>
            <Typography variant="h4">E-Sign Page</Typography>
            <Box mt={3} display="flex" flexDirection="column" alignItems="center">
              <Typography variant="body1" mb={2}>
                Click the button below to e-sign your document
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={handleDigioSign}
                disabled={isDigioLoading}
                sx={{ px: 3, py: 1 }}
              >
                Sign Document
              </Button>
            </Box>
          </SharedCard>
        </SharedContainer>
  
      </>
    );
}